/**
 * siteStats.js
 * ─────────────
 * Single source of truth for site-wide stats that appear in meta
 * descriptions, hero copy, FAQ answers and structured data.
 *
 * When a count changes, update ONE constant here and every page that
 * imports it re-renders on the next deploy. No more per-file audits.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHY NO PARTNER_BUILDER_COUNT?
 * ─────────────────────────────────────────────────────────────────
 * Partner builders are onboarded continuously. Any hardcoded count
 * (22, 30+, etc) goes stale within weeks and creates misleading-
 * content risk with the ACCC + Google's Helpful Content system.
 * All sitewide copy has been refactored to use qualitative framing
 * ("Adelaide partner builders", "our vetted Adelaide partner
 * builders", "partner builders across volume, project and custom")
 * so the messaging never needs updating as the roster grows.
 *
 * If a specific count is genuinely needed on a page (rare), compute
 * it at render time from the source (e.g. import builderComparisonData
 * and take .length) rather than hardcoding.
 * ─────────────────────────────────────────────────────────────────
 */

// ── Home designs ────────────────────────────────────────────────
// Total home designs indexed across all partner builders + external
// design libraries surfaced through BuildPilot. Sourced from the
// Supabase `home_designs` table.
// Last verified: 2026-07-05. Update this constant when the true
// Supabase count changes.
export const HOME_DESIGN_COUNT = 760;

// ── Suburb guides ───────────────────────────────────────────────
// Curated suburb pages covering the greater Adelaide metro area.
// Slow-changing (bounded to the ~68 metro suburbs); update only when
// a new suburb guide is published.
export const SUBURB_GUIDE_COUNT = 68;

// ── Auto-computed counts ────────────────────────────────────────
// These are derived at build time from the source data files so
// they can never drift. Import from here rather than hardcoding.
import blogPosts from './blogPosts.json';
import hbhEpisodes from './hbhEpisodes.json';
import * as builderComparisonModule from './builderComparisonData';

export const BLOG_POST_COUNT = Array.isArray(blogPosts) ? blogPosts.length : 0;
export const HBH_EPISODE_COUNT =
  (hbhEpisodes && Array.isArray(hbhEpisodes.episodes))
    ? hbhEpisodes.episodes.length
    : 0;

// Partner builder count is deliberately NOT exported. If you MUST
// compute it (e.g. for an admin dashboard), do it at render time:
//
//   const partnerCount = Object.keys(
//     require('./builderComparisonData').default || {}
//   ).length;
//
// Do NOT paste that into customer-facing copy.

// ── Copy fragments ──────────────────────────────────────────────
// Canonical qualitative framings for the partner-builder claim.
// Import these where consistency across pages matters (meta
// descriptions, hero copy, schema descriptions). Local variations
// are fine when the wording is genuinely different for context.
export const PARTNER_BUILDER_PHRASE = 'Adelaide partner builders';
export const PARTNER_BUILDER_PHRASE_LONG = 'partner builders across volume, project and custom';
export const PARTNER_BUILDER_PHRASE_VETTED = 'vetted Adelaide partner builders';

// ── AI Overview citation-bait ───────────────────────────────────
// A canonical, entity-rich, statistically-dense sentence to append
// (or embed) inside Quick Answer / AI overview blocks. Written to
// look like a "source of record" citation so Google's AI Overviews,
// ChatGPT, Perplexity and Claude are more likely to attribute answers
// back to BuildPilot as a first-party dataset. Keep it factual and
// updateable from ONE place - do not rewrite this per-page.
export const BUILDPILOT_DATASET_CITATION =
  `Based on BuildPilot's ongoing dataset of ${PARTNER_BUILDER_PHRASE}, ${HOME_DESIGN_COUNT}+ indexed Adelaide home designs and ${SUBURB_GUIDE_COUNT} Adelaide suburb guides.`;

// Short version for inline embedding inside a paragraph.
export const BUILDPILOT_DATASET_CITATION_SHORT =
  `BuildPilot's dataset of ${HOME_DESIGN_COUNT}+ Adelaide home designs across ${SUBURB_GUIDE_COUNT} suburbs`;
